import React, { memo } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Chip, Divider, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getLogisticsMilestoneProgressDisplay } from "../../utils/logisticsMilestoneDetails";
import { getLogisticsProgressData } from "../../utils/logisticsProgress";

const ACTIVE_STAGE_GREEN = "#2E7D32";
const STATUS_COLORS = {
  completed: "#757575",
  "in-progress": ACTIVE_STAGE_GREEN,
  pending: "#9E9E9E",
};

const getProgressPercent = (item) => {
  const value = Number(item?.logisticsProgressPercent ?? 0);

  if (!Number.isFinite(value)) return 0;

  return Math.min(100, Math.max(0, value));
};

const AssetLogisticsStatusCard = ({ item, onClose }) => {
  const { displayName, stageConfig, stages } = getLogisticsProgressData(item);
  const progressPercent = getProgressPercent(item);
  const milestoneDisplay = getLogisticsMilestoneProgressDisplay({
    stageKey: item?.logisticsStage,
    milestone: item?.logisticsMilestone,
    detailText: item?.logisticsDetailText,
    detailDate: item?.logisticsDetailDate,
  });
  const showPrimaryMilestone =
    !milestoneDisplay.hidePrimaryWhenDetail || !milestoneDisplay.detailValue;
  const title = displayName || "Asset Logistics Progress";

  return (
    <Box
      role="dialog"
      aria-label={`${title} logistics status details`}
      sx={{
        position: "relative",
        width: 320,
        maxWidth: "calc(100vw - 32px)",
        p: 2,
        pr: onClose ? 5 : 2,
        borderRadius: 2,
        backgroundColor: "#fff",
      }}
    >
      {onClose && (
        <IconButton
          aria-label="Close logistics status details"
          size="small"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 28,
            height: 28,
            color: "text.secondary",
            "&:hover": {
              color: "text.primary",
              backgroundColor: "action.hover",
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: "primary.main",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.35,
              fontSize: 12,
              color: "text.secondary",
              lineHeight: 1.35,
            }}
          >
            Logistics status details
          </Typography>
        </Box>

        <Chip
          label={`${progressPercent}%`}
          size="small"
          sx={{
            flexShrink: 0,
            height: 24,
            fontWeight: 800,
            fontSize: 11,
            color: ACTIVE_STAGE_GREEN,
            backgroundColor: alpha(ACTIVE_STAGE_GREEN, 0.1),
            border: `1px solid ${alpha(ACTIVE_STAGE_GREEN, 0.22)}`,
          }}
        />
      </Box>

      <Divider sx={{ my: 1.25 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            Current stage
          </Typography>
          <Typography
            variant="caption"
            sx={{
              minWidth: 0,
              maxWidth: "62%",
              fontWeight: 800,
              color: ACTIVE_STAGE_GREEN,
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {stageConfig.label}
          </Typography>
        </Box>

        {showPrimaryMilestone && (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 700 }}
            >
              {milestoneDisplay.primaryLabel}
            </Typography>
            <Typography
              variant="caption"
              title={milestoneDisplay.primaryValue}
              sx={{
                minWidth: 0,
                maxWidth: "62%",
                fontWeight: 800,
                textAlign: "right",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {milestoneDisplay.primaryValue}
            </Typography>
          </Box>
        )}

        {milestoneDisplay.detailValue && (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 700 }}
            >
              {milestoneDisplay.detailLabel}
            </Typography>
            <Typography
              variant="caption"
              title={milestoneDisplay.detailValue}
              sx={{
                minWidth: 0,
                maxWidth: "62%",
                fontWeight: 800,
                textAlign: "right",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {milestoneDisplay.detailValue}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            Next action
          </Typography>
          <Typography
            variant="caption"
            sx={{
              minWidth: 0,
              maxWidth: "62%",
              fontWeight: 800,
              textAlign: "right",
            }}
          >
            {milestoneDisplay.nextAction}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.25 }} />

      <Box
        role="list"
        aria-label="Logistics stage status summary"
        sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
      >
        {stages.map((stage) => {
          const statusColor = STATUS_COLORS[stage.status] || STATUS_COLORS.pending;

          return (
            <Box
              key={stage.key}
              role="listitem"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  minWidth: 0,
                  color: "text.primary",
                  fontWeight: stage.isCurrent ? 800 : 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {stage.title}
              </Typography>
              <Chip
                label={stage.isFinalMilestoneReached ? "Completed" : stage.statusLabel}
                size="small"
                variant={stage.isCurrent ? "filled" : "outlined"}
                sx={{
                  flexShrink: 0,
                  height: 20,
                  fontSize: 10,
                  fontWeight: 800,
                  color: stage.isCurrent ? "#fff" : statusColor,
                  backgroundColor: stage.isCurrent
                    ? statusColor
                    : alpha(statusColor, 0.08),
                  borderColor: alpha(statusColor, 0.28),
                  "& .MuiChip-label": {
                    px: 0.8,
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default memo(AssetLogisticsStatusCard);
